import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Modal,
  Stack,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import asyncService from '../../../datahub/asyncService';
import selectUser from '../../store/selectors/appSelector';
import { BLACK, PRIMARY_PURPLE } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import theme from '../../theme/theme';
import FreeTextSearchFilter from '../FreeTextSearchFilter';
import { SearchableSelectDropdownFilterDeprecated } from '../SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import { SelectDropdownFilter } from '../SelectDropdownFilter/SelectDropdownFilter';

import {
  API_CONFIG,
  FILTER_DEFAULTS,
  FILTER_KEYS,
  INVALID_WORKER_FILTERS,
  SORT_OPTIONS,
  UI_TEXT,
} from './constants/modalConstants';
import { processFiltersToCleanFormat } from './utils/filterUtils';
import InvalidWorkersSection from './InvalidWorkersSection';
import JobInformationCard from './JobInformationCard';
import RecommendWorkerCard from './RecommendWorkerCard';
import {
  agencyAPICallback,
  filterConfig,
  getLastLoginOptions,
  getSortByOptions,
  recruiterAPICallback,
  // pastPlacementOptions,
  // placementScoreOptions,
  // reliabilityOptions,
} from './recommendWorkerHelper';
import {
  cardStyle,
  filterInputStyle,
  headerStyle,
  maxRequestNumReachedStyle,
  modalStyle,
} from './styles';

const RecommendWorkerModal = ({ isOpen, onClose, jobOrder, onSuccess }) => {
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const [candidatesList, setCandidatesList] = useState([]);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.NEWEST); // Default to newest logins first
  const candidatesListRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [numOfRequests, setNumOfRequests] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Cursor pagination state
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const lastSentCursorRef = useRef(null);
  const lastReceivedCursorRef = useRef(null);
  const nameSearchRef = useRef(FILTER_DEFAULTS.NAME);
  const [hasSuccessfulOperation, setHasSuccessfulOperation] = useState(false);
  const [filters, setFilters] = useState({
    [FILTER_KEYS.NAME]: FILTER_DEFAULTS.NAME,
    // reliabilityScore: 50,
    placementScore: FILTER_DEFAULTS.PLACEMENT_SCORE,
    pastPlacementCount: FILTER_DEFAULTS.PAST_PLACEMENT_COUNT,
    [FILTER_KEYS.LAST_LOGIN_DATE]: FILTER_DEFAULTS.LAST_LOGIN_DATE,
    [FILTER_KEYS.AGENCY]: FILTER_DEFAULTS.AGENCY,
    [FILTER_KEYS.RECRUITER]: FILTER_DEFAULTS.RECRUITER,
    [FILTER_KEYS.DISTANCE]: FILTER_DEFAULTS.DISTANCE,
    [FILTER_KEYS.NO_FUTURE_WORK]: FILTER_DEFAULTS.NO_FUTURE_WORK,
    [FILTER_KEYS.INDUSTRY_MEETS_PREF]: FILTER_DEFAULTS.INDUSTRY_MEETS_PREF,
    [FILTER_KEYS.PAY_RATE_MEETS_PREF]: FILTER_DEFAULTS.PAY_RATE_MEETS_PREF,
    [FILTER_KEYS.SHIFT_TIME_MEETS_PREF]: FILTER_DEFAULTS.SHIFT_TIME_MEETS_PREF,
    [FILTER_KEYS.WOTC_ELIGIBLE]: FILTER_DEFAULTS.WOTC_ELIGIBLE,
    [FILTER_KEYS.URBAN_YOUTH_CERTIFIED]: FILTER_DEFAULTS.URBAN_YOUTH_CERTIFIED,
    [FILTER_KEYS.SORT_BY]: SORT_OPTIONS.NEWEST,
    [INVALID_WORKER_FILTERS.OVERTIME_BLOCKED]: FILTER_DEFAULTS.OVERTIME_BLOCKED,
    [INVALID_WORKER_FILTERS.SUSPENDED]: FILTER_DEFAULTS.SUSPENDED,
    [INVALID_WORKER_FILTERS.NOT_QUALIFIED]: FILTER_DEFAULTS.NOT_QUALIFIED,
    [INVALID_WORKER_FILTERS.CONFLICTING_SCHEDULE]: FILTER_DEFAULTS.CONFLICTING_SCHEDULE,
    [INVALID_WORKER_FILTERS.INCOMPLETE_ONBOARDING]: FILTER_DEFAULTS.INCOMPLETE_ONBOARDING,
    [INVALID_WORKER_FILTERS.IMPROPER_EOR_OR_JOB_FULL]: FILTER_DEFAULTS.IMPROPER_EOR_OR_JOB_FULL,
  });

  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const token = user?.token;
  const refreshToken = user?.refreshToken;

  // Generate sort options with language conversion
  const sortByOptions = getSortByOptions(LanguageConverterV2);

  // Generate last login options with language conversion
  const lastLoginOptions = getLastLoginOptions(LanguageConverterV2);

  const memoizedRecruiterAPICallback = useMemo(
    () => recruiterAPICallback(filters.recruiter),
    [filters.recruiter]
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
    },
    []
  );

  const safeCandidatesListSet = (data) => {
    if (isMountedRef.current) {
      setCandidatesList(data);
      candidatesListRef.current = data;
    }
  };

  const safeSetLoading = (state) => {
    if (isMountedRef.current) {
      setLoading(state);
    }
  };
  const safeSetNumOfRequests = (value) => {
    if (isMountedRef.current) {
      setNumOfRequests(value);
    }
  };

  const handleSuccess = useCallback(() => {
    setHasSuccessfulOperation(true);
  }, []);

  const handleClose = useCallback(() => {
    if (hasSuccessfulOperation && onSuccess) {
      onSuccess();
    }
    setHasSuccessfulOperation(false);
    onClose();
  }, [hasSuccessfulOperation, onSuccess, onClose]);

  const jobId = jobOrder?._id;
  const fetchCandidates = useCallback(
    async (customFilters = null, resetPagination = false, loadingIndicator = true) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrentRequest = () => isMountedRef.current && requestId === requestIdRef.current;

      if (loadingIndicator) {
        safeSetLoading(true);
      }
      const cleanFilters = processFiltersToCleanFormat(customFilters, filterConfig);

      // Reset pagination state if this is a new search
      if (resetPagination) {
        setCandidatesList([]);
        candidatesListRef.current = [];
        setIsFetchingMore(false);
        setHasMoreResults(true);
        lastSentCursorRef.current = null;
        lastReceivedCursorRef.current = null;
        safeSetNumOfRequests(0);
      }

      const fetchMoreCandidates = async (currentCursor = null, accumulatedCandidates = []) => {
        if (!isCurrentRequest()) return accumulatedCandidates;

        // Check if we're about to send the same cursor we just sent (infinite loop detection)
        if (currentCursor && lastSentCursorRef.current === currentCursor) {
          setHasMoreResults(false);
          setIsFetchingMore(false);
          return accumulatedCandidates;
        }

        // Set fetching more state if this is not the first request
        if (currentCursor) {
          setIsFetchingMore(true);
        }

        // Increment request counter
        safeSetNumOfRequests((prev) => prev + 1);

        const hideFilters = {};
        hideFilters.hideOTRisk = !customFilters?.[INVALID_WORKER_FILTERS.OVERTIME_BLOCKED];
        hideFilters.hideSuspended = !customFilters?.[INVALID_WORKER_FILTERS.SUSPENDED];
        hideFilters.hideNotQualified = !customFilters?.[INVALID_WORKER_FILTERS.NOT_QUALIFIED];
        hideFilters.hideConflict = !customFilters?.[INVALID_WORKER_FILTERS.CONFLICTING_SCHEDULE];
        hideFilters.hideOnboarding = !customFilters?.[INVALID_WORKER_FILTERS.INCOMPLETE_ONBOARDING];
        hideFilters.hideJobAvailability =
          !customFilters?.[INVALID_WORKER_FILTERS.IMPROPER_EOR_OR_JOB_FULL];

        const payload = {
          jobOrderId: jobId,
          filters: {
            filters: cleanFilters,
            limit: API_CONFIG.RESULTS_PER_REQUEST,
            ...(customFilters?.sortBy && {
              sortBy: [
                {
                  field: 'lastLoginDate',
                  descending: customFilters.sortBy === SORT_OPTIONS.NEWEST,
                },
              ],
            }),
          },
          ...(currentCursor && { cursorLocation: currentCursor }),
          ...hideFilters,
        };

        try {
          const result = await new Promise((resolve, reject) => {
            asyncService({
              httpMethod: 'POST',
              route: 'users/v2/find-workers-for-joborders',
              data: payload,
              onSuccess: (d) => {
                const rawApiQueryResult = d?.data?.candidates || [];
                const responseCursor = d?.data?.cursorLocation;

                resolve({
                  candidates: rawApiQueryResult,
                  cursor: responseCursor,
                });
              },
              onError: (error) => {
                reject(error);
              },
              dispatch,
              token,
              refreshToken,
            });
          });

          if (!isCurrentRequest()) return accumulatedCandidates;

          // Update last sent cursor reference for loop detection after API call
          lastSentCursorRef.current = currentCursor;

          lastReceivedCursorRef.current = result.cursor;

          const newCandidates = [...accumulatedCandidates, ...result.candidates];
          const newTotalFetched = accumulatedCandidates.length + result.candidates.length;

          // Update state with accumulated results
          safeCandidatesListSet(newCandidates);

          // Check if we should continue fetching
          const shouldContinue =
            newTotalFetched < API_CONFIG.MAX_RESULTS && // Haven't reached max results yet
            result.cursor && // Have a cursor for the next request
            isCurrentRequest();
          if (shouldContinue) {
            // Continue fetching with the new cursor
            // This call is triggered during initial search or filter application
            // to automatically fetch more results until MAX_RESULTS (100) is reached
            return fetchMoreCandidates(result.cursor, newCandidates);
          }

          // We're done - either we have 100+ results or no more data
          setIsFetchingMore(false);

          // If no candidates are returned, there are no more results available
          if (result.candidates.length === 0) {
            setHasMoreResults(false);
          }

          return newCandidates;
        } catch (error) {
          if (isCurrentRequest()) {
            setIsFetchingMore(false);
          }
          return accumulatedCandidates;
        }
      };

      try {
        // This branch is triggered when resetPagination=false (i.e., "Find More Workers" button)
        // and there's a cursor from previous API response to continue pagination
        if (lastReceivedCursorRef.current) {
          await fetchMoreCandidates(lastReceivedCursorRef.current, candidatesListRef.current);
        } else {
          await fetchMoreCandidates();
        }
      } catch (error) {
        if (isCurrentRequest()) {
          safeCandidatesListSet([]);
        }
      } finally {
        if (isCurrentRequest()) {
          safeSetLoading(false);
          setNumOfRequests(0);
          setIsInitialLoad(false); // Mark that initial load is complete
        }
      }
    },
    [dispatch, token, refreshToken, jobId]
  );

  useEffect(() => {
    if (isOpen) {
      setIsInitialLoad(true); // Reset initial load state when modal opens
      fetchCandidates(filters, true); // Reset pagination when modal opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFilterChange = useCallback(
    (key, value) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setFilters]
  );
  const handleInvalidWorkersChange = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      ...newValue,
    }));
  }, []);

  const handleNameSearchChange = useCallback(
    (searchTerm) => {
      handleFilterChange(FILTER_KEYS.NAME, searchTerm[0]?.value || '');
    },
    [handleFilterChange]
  );

  const handleNameInputChange = useCallback((value) => {
    nameSearchRef.current = value;
  }, []);

  const handleApplyFilters = () => {
    const nextFilters = {
      ...filters,
      [FILTER_KEYS.NAME]: nameSearchRef.current,
    };
    setFilters(nextFilters);
    setIsInitialLoad(false);
    fetchCandidates(nextFilters, true); // Reset pagination when applying new filters
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography
          sx={{
            fontFamily: theme.components.recommendWorkerModal.typography.title.fontFamily,
            fontSize: theme.components.recommendWorkerModal.typography.title.fontSize,
            color: theme.components.recommendWorkerModal.palette.title.fontColor,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            pb: 1,
            mb: 1,
          }}
        >
          {LanguageConverterV2(UI_TEXT.FIND_WORKERS)}
        </Typography>

        <Grid
          container
          spacing={3}
          direction={{ xs: 'column', lg: 'row' }}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: { xs: 'auto', lg: 'hidden' },
            flexWrap: 'nowrap',
            alignContent: 'flex-start',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Grid
            item
            xs={12}
            lg={5}
            sx={{
              minHeight: 0,
              display: 'flex',
              flex: { xs: '0 0 auto', lg: '1 1 0' },
              maxWidth: { xs: '100%', lg: '41.666667%' },
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: { xs: 'auto', lg: '100%' },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: { xs: '0 0 auto', lg: 1 },
                  minHeight: 0,
                  overflow: { xs: 'visible', lg: 'auto' },
                }}
              >
                <Box>
                  <JobInformationCard jobOrder={jobOrder} />
                </Box>
                <Card sx={{ flex: 1 }} elevation={0}>
                  <CardContent>
                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.SEARCH)}
                      </Typography>
                      <FreeTextSearchFilter
                        disabled={loading && !isInitialLoad}
                        wrapperSx={{ width: '100%' }}
                        placeholder={LanguageConverterV2(UI_TEXT.EMPLOYEE_NAME)}
                        onValueChange={handleNameSearchChange}
                        onInputChange={handleNameInputChange}
                        field="name"
                        operation="icontains"
                        initialValue={{ value: filters.name }}
                      />
                    </Box>
                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.SEARCH_AGENCY)}
                      </Typography>
                      <SearchableSelectDropdownFilterDeprecated
                        disabled={loading && !isInitialLoad}
                        placeholder={LanguageConverterV2(UI_TEXT.SEARCH_AGENCY)}
                        onValueChange={(agencyId) =>
                          handleFilterChange(FILTER_KEYS.AGENCY, agencyId)
                        }
                        optionsAPICallback={agencyAPICallback}
                        getOptionLabel={(option) => option.name}
                        initialValue={filters.agency}
                        wrapperSx={{ width: '100%' }}
                      />
                    </Box>

                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.SEARCH_RECRUITER)}
                      </Typography>
                      <SearchableSelectDropdownFilterDeprecated
                        disabled={loading && !isInitialLoad}
                        placeholder={LanguageConverterV2(UI_TEXT.SEARCH_RECRUITER)}
                        onValueChange={(recruiterId) =>
                          handleFilterChange(
                            FILTER_KEYS.RECRUITER,
                            recruiterId && (!Array.isArray(recruiterId) || recruiterId.length)
                              ? recruiterId
                              : null
                          )
                        }
                        optionsAPICallback={memoizedRecruiterAPICallback}
                        getOptionLabel={(option) => option.name}
                        initialValue={filters.recruiter}
                        wrapperSx={{ width: '100%' }}
                      />
                    </Box>

                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.DISTANCE)}
                      </Typography>
                      <TextField
                        disabled={loading && !isInitialLoad}
                        type="number"
                        placeholder={LanguageConverterV2(UI_TEXT.DISTANCE)}
                        value={filters.distance ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const parsedDistance = parseInt(val, 10);
                          handleFilterChange(
                            FILTER_KEYS.DISTANCE,
                            val === '' || Number.isNaN(parsedDistance) ? null : parsedDistance
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e') e.preventDefault();
                        }}
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ ...filterInputStyle, width: '100%' }}
                      />
                    </Box>

                    <Box
                      sx={{
                        marginBottom: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.noFutureWork === 0}
                            onChange={() =>
                              handleFilterChange(
                                FILTER_KEYS.NO_FUTURE_WORK,
                                filters.noFutureWork === 0 ? null : 0
                              )
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.NO_FUTURE_WORK)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.industryMeetsPref}
                            onChange={() =>
                              handleFilterChange(
                                FILTER_KEYS.INDUSTRY_MEETS_PREF,
                                !filters.industryMeetsPref
                              )
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.INDUSTRY_MEETS_PREF)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.payRateMeetsPref}
                            onChange={() =>
                              handleFilterChange(
                                FILTER_KEYS.PAY_RATE_MEETS_PREF,
                                !filters.payRateMeetsPref
                              )
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.PAY_RATE_MEETS_PREF)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.shiftTimeMeetsPref}
                            onChange={() =>
                              handleFilterChange(
                                FILTER_KEYS.SHIFT_TIME_MEETS_PREF,
                                !filters.shiftTimeMeetsPref
                              )
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.SHIFT_TIME_MEETS_PREF)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.wotcEligible}
                            onChange={() =>
                              handleFilterChange(FILTER_KEYS.WOTC_ELIGIBLE, !filters.wotcEligible)
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.WOTC_ELIGIBLE)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filters.urbanYouthCertified}
                            onChange={() =>
                              handleFilterChange(
                                FILTER_KEYS.URBAN_YOUTH_CERTIFIED,
                                !filters.urbanYouthCertified
                              )
                            }
                            disabled={loading && !isInitialLoad}
                          />
                        }
                        label={LanguageConverterV2(UI_TEXT.URBAN_YOUTH_CERTIFIED)}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            fontFamily: PRIMARY_FONT[400],
                            color: BLACK[100],
                          },
                        }}
                      />
                    </Box>

                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.LAST_LOGIN)}
                      </Typography>
                      <SelectDropdownFilter
                        disabled={loading && !isInitialLoad}
                        placeholder={LanguageConverterV2(UI_TEXT.LAST_LOGIN)}
                        onValueChange={(value) =>
                          handleFilterChange(FILTER_KEYS.LAST_LOGIN_DATE, value)
                        }
                        options={lastLoginOptions}
                        getOptionLabel={(option) => option?.name}
                        wrapperSx={{ width: '100%' }}
                        initialValue={
                          lastLoginOptions.find(
                            (option) => option.value === filters.lastLoginDate
                          ) || null
                        }
                      />
                    </Box>
                    <Box sx={cardStyle}>
                      <Typography sx={headerStyle} gutterBottom>
                        {LanguageConverterV2(UI_TEXT.SORT_BY)}
                      </Typography>
                      <SelectDropdownFilter
                        disabled={loading && !isInitialLoad}
                        placeholder={LanguageConverterV2(UI_TEXT.SORT_BY)}
                        onValueChange={(value) => {
                          setSortBy(value);
                          handleFilterChange(FILTER_KEYS.SORT_BY, value);
                        }}
                        options={sortByOptions}
                        getOptionLabel={(option) => option?.name}
                        wrapperSx={{ width: '100%' }}
                        initialValue={
                          sortByOptions.find((option) => option.value === sortBy) || null
                        }
                      />
                    </Box>

                    <Stack spacing={2}>
                      <InvalidWorkersSection
                        value={{
                          [INVALID_WORKER_FILTERS.OVERTIME_BLOCKED]:
                            filters[INVALID_WORKER_FILTERS.OVERTIME_BLOCKED],
                          [INVALID_WORKER_FILTERS.SUSPENDED]:
                            filters[INVALID_WORKER_FILTERS.SUSPENDED],
                          [INVALID_WORKER_FILTERS.NOT_QUALIFIED]:
                            filters[INVALID_WORKER_FILTERS.NOT_QUALIFIED],
                          [INVALID_WORKER_FILTERS.CONFLICTING_SCHEDULE]:
                            filters[INVALID_WORKER_FILTERS.CONFLICTING_SCHEDULE],
                          [INVALID_WORKER_FILTERS.INCOMPLETE_ONBOARDING]:
                            filters[INVALID_WORKER_FILTERS.INCOMPLETE_ONBOARDING],
                          [INVALID_WORKER_FILTERS.IMPROPER_EOR_OR_JOB_FULL]:
                            filters[INVALID_WORKER_FILTERS.IMPROPER_EOR_OR_JOB_FULL],
                        }}
                        onChange={handleInvalidWorkersChange}
                        disabled={loading && !isInitialLoad}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flexShrink: 0, mt: 2, alignSelf: 'flex-end' }}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  loading={loading && !isInitialLoad}
                  sx={{
                    fontFamily: PRIMARY_FONT[700],
                    textTransform: 'none',
                    padding: theme.components.recommendWorkerModal.layout.button.padding,
                    minWidth: theme.components.recommendWorkerModal.layout.button.minWidth,

                    '& .MuiLoadingButton-startIcon': {
                      marginRight: theme.components.recommendWorkerModal.layout.button.iconMargin,
                    },
                  }}
                >
                  {LanguageConverterV2(UI_TEXT.APPLY_FILTERS)}
                </LoadingButton>
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            lg={7}
            sx={{
              minHeight: 0,
              display: 'flex',
              flex: { xs: '0 0 auto', lg: '1 1 0' },
              maxWidth: { xs: '100%', lg: '58.333333%' },
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: { xs: 'auto', lg: '100%' },
                minHeight: { xs: 280, lg: 0 },
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              {numOfRequests > API_CONFIG.MAX_REQUESTS_WARNING && (
                <Box
                  sx={{
                    p: 1,
                    mb: 1,
                    textAlign: 'center',
                    backgroundColor:
                      theme.components.recommendWorkerModal.palette.warning.backgroundColor,
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" sx={maxRequestNumReachedStyle}>
                    {LanguageConverterV2(UI_TEXT.LARGE_SEARCH_WARNING)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: { xs: 240, lg: 0 },
                  overflow: { xs: 'visible', lg: 'auto' },
                  border: `1px solid ${theme.components.recommendWorkerModal.palette.border.color}`,
                  borderRadius: theme.components.recommendWorkerModal.layout.card.borderRadius,
                  p: { xs: 1.5, lg: theme.components.recommendWorkerModal.layout.card.padding },
                }}
              >
                {candidatesList?.length === 0 && loading && (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {LanguageConverterV2(UI_TEXT.LOADING_MORE_CANDIDATES)}
                    </Typography>
                  </Box>
                )}
                {candidatesList?.length === 0 && !loading && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography color="text.secondary">
                      {LanguageConverterV2(UI_TEXT.NO_CANDIDATES)}
                    </Typography>
                  </Box>
                )}
                {candidatesList?.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {candidatesList?.map((candidate, idx) => (
                        <RecommendWorkerCard
                          key={candidate?.id || candidate?.userId || idx}
                          candidate={candidate}
                          loading={loading}
                          user={user}
                          jobOrderId={jobId}
                          onSuccess={handleSuccess}
                        />
                      ))}
                    </Box>

                    {isFetchingMore && (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          {LanguageConverterV2(UI_TEXT.LOADING_MORE_CANDIDATES)}
                        </Typography>
                      </Box>
                    )}
                    {hasMoreResults && !isFetchingMore && (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => fetchCandidates(filters, false)}
                          disabled={loading && !isInitialLoad}
                          sx={{
                            color: PRIMARY_PURPLE[70],
                            borderColor: PRIMARY_PURPLE[70],
                            '&:hover': {
                              borderColor: PRIMARY_PURPLE[70],
                              backgroundColor:
                                theme.components.recommendWorkerModal.palette.hover.backgroundColor,
                            },
                            textTransform: 'none',
                            fontWeight: '700',
                            padding:
                              theme.components.recommendWorkerModal.layout.button.loadingPadding,
                          }}
                        >
                          {LanguageConverterV2(UI_TEXT.FIND_MORE_WORKERS)}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <Box sx={{ flexShrink: 0, textAlign: 'right', mt: 2 }}>
                <Button
                  variant="outlined"
                  sx={{
                    color: PRIMARY_PURPLE[70],
                    borderColor: PRIMARY_PURPLE[70],
                    '&:hover': {
                      borderColor: PRIMARY_PURPLE[70],
                    },
                    textTransform: 'none',
                    fontWeight: '700',
                  }}
                  onClick={handleClose}
                >
                  {LanguageConverterV2(UI_TEXT.CLOSE)}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

RecommendWorkerModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  jobOrder: PropTypes.shape({ _id: PropTypes.string }),
  onSuccess: PropTypes.func,
};

export default RecommendWorkerModal;
